// src/pages/Media.jsx
import React, {
  useEffect, useState, useMemo, useCallback, useRef,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import {
  collection, query, orderBy, limit, onSnapshot,
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  normalizeMedia,
  formatRelativeTime,
} from '../utils/feedHelpers';
import MediaLightbox from '../components/home/MediaLightbox';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const MEDIA_FETCH_LIMIT = 200;

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const toMillis = (v) =>
  v?.toDate?.()?.getTime?.() ||
  (typeof v === 'number' ? v : null) ||
  0;

/**
 * Group label for date bucketing.
 */
const dateGroupLabel = (ts) => {
  if (!ts) return 'Older';
  const now = new Date();
  const d = new Date(ts);
  const sameDay = now.toDateString() === d.toDateString();
  if (sameDay) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (yesterday.toDateString() === d.toDateString()) return 'Yesterday';
  if (now - d < 7 * 24 * 60 * 60 * 1000) return 'This week';
  if (now - d < 30 * 24 * 60 * 60 * 1000) return 'This month';
  return 'Older';
};

/**
 * Canonical ordering for the date-bucket sections.
 * Must match the set of strings returned by `dateGroupLabel`.
 */
const DATE_GROUP_ORDER = ['Today', 'Yesterday', 'This week', 'This month', 'Older'];

/* ================================================================== */
/* Media Page                                                          */
/* ================================================================== */

const Media = () => {
  const { user, isAdmin, isApproved, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* ---------------- Filters from URL ---------------- */
  const initialFilter = ['image', 'video', 'all'].includes(
    searchParams.get('type'),
  )
    ? searchParams.get('type')
    : 'all';
  const initialScope = ['all', 'mine'].includes(searchParams.get('scope'))
    ? searchParams.get('scope')
    : 'all';
  const initialSearch = searchParams.get('q') || '';

  const [filter, setFilter] = useState(initialFilter);
  const [scope, setScope] = useState(initialScope);
  const [search, setSearch] = useState(initialSearch);
  const [groupByDate, setGroupByDate] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const searchInputRef = useRef(null);

  /* ---------------- Sync state to URL ---------------- */
  useEffect(() => {
    const next = new URLSearchParams();
    if (filter !== 'all') next.set('type', filter);
    if (scope !== 'all') next.set('scope', scope);
    if (search.trim()) next.set('q', search.trim());
    setSearchParams(next, { replace: true });
  }, [filter, scope, search, setSearchParams]);

  /* ---------------- Live posts listener ---------------- */
  useEffect(() => {
    if (!user || !isApproved) {
      setPosts([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const q = query(
      collection(db, 'posts'),
      orderBy('createdAt', 'desc'),
      limit(MEDIA_FETCH_LIMIT),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const visible = all.filter((p) => {
          if (isAdmin) return true;
          if (p.authorId === user.uid) return true;
          return p.approved !== false && p.visibility !== 'private';
        });

        setPosts(visible);
        setLoading(false);
      },
      (err) => {
        console.warn('media listener:', err);
        setError('Failed to load media.');
        setLoading(false);
      },
    );

    return () => unsub();
  }, [user, isApproved, isAdmin]);

  /* ---------------- Flatten into media items ---------------- */
  const mediaItems = useMemo(() => {
    const out = [];
    for (const post of posts) {
      const media = normalizeMedia(post);
      if (!media.length) continue;

      if (scope === 'mine' && post.authorId !== user?.uid) continue;

      const ts = toMillis(post.postDate) || toMillis(post.createdAt);

      media.forEach((m) => {
        out.push({
          url: m.url,
          type: m.type || 'image',
          isCover: !!m.isCover,
          postId: post.id,
          postAuthor: post.authorName,
          postAuthorId: post.authorId,
          postAuthorPhoto: post.authorPhoto,
          caption: (post.content || '').slice(0, 200),
          createdAt: ts,
        });
      });
    }
    return out.sort((a, b) => b.createdAt - a.createdAt);
  }, [posts, scope, user?.uid]);

  /* ---------------- Filter + search ---------------- */
  const filtered = useMemo(() => {
    let list = mediaItems;
    if (filter !== 'all') list = list.filter((m) => m.type === filter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (m) =>
          (m.caption || '').toLowerCase().includes(q) ||
          (m.postAuthor || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [mediaItems, filter, search]);

  /* ---------------- Grouped by date (optional) ---------------- */
  /**
   * Because `filtered` is already sorted newest-first, bucketing it by
   * `dateGroupLabel` and then iterating in `DATE_GROUP_ORDER` yields
   * exactly `filtered` in its original order. That invariant is what
   * lets the grouped tiles reuse the flat index (`idx`) for the
   * lightbox without remapping.
   */
  const grouped = useMemo(() => {
    if (!groupByDate) return null;
    const map = new Map();
    filtered.forEach((item, idx) => {
      const label = dateGroupLabel(item.createdAt);
      if (!map.has(label)) map.set(label, []);
      map.get(label).push({ item, idx });
    });
    return DATE_GROUP_ORDER
      .filter((label) => map.has(label))
      .map((label) => ({ label, items: map.get(label) }));
  }, [filtered, groupByDate]);

  /* ---------------- Lightbox ---------------- */
  const openLightbox = useCallback(
    (startIndex) => {
      setLightbox({ items: filtered, startIndex });
    },
    [filtered],
  );
  const closeLightbox = useCallback(() => setLightbox(null), []);

  /* ---------------- Counts ---------------- */
  const counts = useMemo(() => {
    const images = mediaItems.filter((m) => m.type === 'image').length;
    const videos = mediaItems.filter((m) => m.type === 'video').length;
    return { all: mediaItems.length, image: images, video: videos };
  }, [mediaItems]);

  /* ---------------- Keyboard shortcuts ---------------- */
  useEffect(() => {
    const onKey = (e) => {
      // Ignore when typing in an input
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') {
        if (e.key === 'Escape') {
          document.activeElement.blur();
        }
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        const el = searchInputRef.current;
        if (el) {
          el.focus();
          // Select existing text so typing replaces it (standard behavior)
          try { el.select(); } catch { /* noop */ }
        }
      }
      if (e.key === 'Escape') {
        setSearch('');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* ---------------- Loading / auth gates ---------------- */
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="w-12 h-12 border-4 border-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-100 dark:bg-gray-900">
        <div className="text-center max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-orange/10 flex items-center justify-center">
            <i className="fas fa-images text-2xl text-orange" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Media Gallery
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Sign in to browse photos and videos shared by the community.
          </p>
          <Link
            to="/login"
            className="inline-block bg-orange hover:bg-orange-dark text-white px-6 py-2 rounded-full text-sm font-medium transition"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  if (!isApproved) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gray-100 dark:bg-gray-900">
        <div className="text-center max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
            <i className="fas fa-clock text-2xl text-yellow-600" aria-hidden="true" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Account Pending Approval
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            You'll be able to browse the media gallery once an admin approves your account.
          </p>
        </div>
      </div>
    );
  }

  /* ---------------- Main render ---------------- */
  return (
    <>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-4 md:py-6 px-3 md:px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-1">
              <i className="fas fa-images text-orange text-xl" aria-hidden="true" />
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Media Gallery
              </h1>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              All photos and videos shared in posts by the community.
            </p>
          </div>

          {/* Controls */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-3 md:p-4 mb-5 space-y-3">
            {/* Search */}
            <div className="relative">
              <i
                className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                aria-hidden="true"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by caption or author… (press /)"
                className="w-full pl-9 pr-10 py-2 border rounded-lg text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange"
                aria-label="Search media"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-orange hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                >
                  <i className="fas fa-times-circle text-sm" aria-hidden="true" />
                </button>
              )}
            </div>

            {/* Type + scope filters */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { id: 'all', label: 'All', count: counts.all, icon: 'fa-layer-group' },
                { id: 'image', label: 'Photos', count: counts.image, icon: 'fa-image' },
                { id: 'video', label: 'Videos', count: counts.video, icon: 'fa-video' },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  aria-pressed={filter === f.id}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${
                    filter === f.id
                      ? 'bg-orange text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <i className={`fas ${f.icon} text-[10px]`} aria-hidden="true" />
                  {f.label}
                  <span
                    className={`ml-0.5 text-[10px] px-1.5 rounded-full ${
                      filter === f.id
                        ? 'bg-white/25'
                        : 'bg-white dark:bg-gray-800'
                    }`}
                  >
                    {f.count}
                  </span>
                </button>
              ))}

              <span className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />

              <button
                type="button"
                onClick={() => setScope('all')}
                aria-pressed={scope === 'all'}
                className={`text-xs px-3 py-1.5 rounded-full transition ${
                  scope === 'all'
                    ? 'bg-orange text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                All Members
              </button>
              <button
                type="button"
                onClick={() => setScope('mine')}
                aria-pressed={scope === 'mine'}
                className={`text-xs px-3 py-1.5 rounded-full transition ${
                  scope === 'mine'
                    ? 'bg-orange text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <i className="fas fa-user mr-1" aria-hidden="true" />
                Mine
              </button>

              <span className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />

              <button
                type="button"
                onClick={() => setGroupByDate((g) => !g)}
                aria-pressed={groupByDate}
                title={groupByDate ? 'Ungroup by date' : 'Group by date'}
                className={`text-xs px-3 py-1.5 rounded-full transition flex items-center gap-1.5 ${
                  groupByDate
                    ? 'bg-orange text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <i className="fas fa-calendar-day" aria-hidden="true" />
                <span className="hidden sm:inline">By date</span>
              </button>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <MediaSkeleton />
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center text-sm text-red-700 dark:text-red-200">
              <i className="fas fa-circle-exclamation text-2xl mb-2 block" aria-hidden="true" />
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState
              scope={scope}
              filter={filter}
              hasSearch={!!search.trim()}
              onClear={() => {
                setSearch('');
                setFilter('all');
                setScope('all');
              }}
            />
          ) : groupByDate && grouped ? (
            <div className="space-y-6">
              {grouped.map((group) => (
                <div key={group.label}>
                  <div className="flex items-center gap-3 mb-3">
                    <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                      {group.label}
                    </h2>
                    <span className="text-xs text-gray-500">
                      {group.items.length} item
                      {group.items.length === 1 ? '' : 's'}
                    </span>
                    <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
                    {group.items.map(({ item, idx }) => (
                      <MediaTile
                        key={`${item.postId}-${item.url}-${idx}`}
                        item={item}
                        onOpen={() => openLightbox(idx)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
                <AnimatePresence mode="popLayout">
                  {filtered.map((m, i) => (
                    <MediaTile
                      key={`${m.postId}-${m.url}-${i}`}
                      item={m}
                      onOpen={() => openLightbox(i)}
                    />
                  ))}
                </AnimatePresence>
              </div>
              <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-6">
                Showing {filtered.length} item{filtered.length === 1 ? '' : 's'}
                {posts.length >= MEDIA_FETCH_LIMIT && ' (latest 200 posts)'}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <MediaLightbox
          media={lightbox.items}
          initialIndex={lightbox.startIndex}
          onClose={closeLightbox}
        />
      )}
    </>
  );
};

/* ================================================================== */
/* Media Tile                                                          */
/* ================================================================== */

const MediaTile = ({ item, onOpen }) => {
  const [loaded, setLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  // Reset loading state if the tile is re-used for a different URL
  // (defensive; the key scheme makes this unlikely in practice).
  useEffect(() => {
    setLoaded(false);
    setImgError(false);
  }, [item.url]);

  return (
    <motion.button
      type="button"
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.15 }}
      onClick={onOpen}
      className="relative group aspect-square rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-orange focus:ring-inset"
      aria-label={`View ${item.type === 'video' ? 'video' : 'photo'}${
        item.postAuthor ? ` by ${item.postAuthor}` : ''
      }`}
    >
      {item.type === 'video' ? (
        <>
          <video
            src={item.url}
            className="absolute inset-0 w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center group-hover:scale-110 transition-transform">
              <i className="fas fa-play text-white text-sm" aria-hidden="true" />
            </div>
          </div>
        </>
      ) : (
        <>
          {!loaded && !imgError && (
            <div className="absolute inset-0 animate-pulse bg-gray-200 dark:bg-gray-700" />
          )}
          {imgError ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
              <i
                className="fas fa-image text-gray-400 text-2xl"
                aria-hidden="true"
              />
            </div>
          ) : (
            <img
              src={item.url}
              alt=""
              loading="lazy"
              onLoad={() => setLoaded(true)}
              onError={() => setImgError(true)}
              className={`absolute inset-0 w-full h-full object-cover transition-all duration-300 group-hover:scale-105 ${
                loaded ? 'opacity-100' : 'opacity-0'
              }`}
            />
          )}
        </>
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

      {/* Cover badge */}
      {item.isCover && (
        <div className="absolute top-1.5 left-1.5 bg-orange text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shadow">
          <i className="fas fa-star text-[7px]" aria-hidden="true" />
          Cover
        </div>
      )}

      {/* Video badge */}
      {item.type === 'video' && (
        <div className="absolute top-1.5 right-1.5 bg-black/70 text-white text-[9px] font-semibold px-1.5 py-0.5 rounded-full">
          <i className="fas fa-video text-[8px] mr-0.5" aria-hidden="true" />
          Video
        </div>
      )}

      {/* Author + time on hover */}
      <div className="absolute bottom-0 left-0 right-0 p-2 text-left opacity-0 group-hover:opacity-100 transition-opacity">
        <p className="text-white text-[11px] font-medium truncate drop-shadow">
          {item.postAuthor || 'User'}
        </p>
        <p className="text-white/70 text-[9px]">
          {item.createdAt ? formatRelativeTime(item.createdAt) : ''}
        </p>
      </div>
    </motion.button>
  );
};

/* ================================================================== */
/* Empty state                                                         */
/* ================================================================== */

const EmptyState = ({ scope, filter, hasSearch, onClear }) => {
  const message = hasSearch
    ? 'No media matches your search.'
    : scope === 'mine'
    ? "You haven't uploaded any media yet."
    : filter === 'video'
    ? 'No videos have been shared yet.'
    : filter === 'image'
    ? 'No photos have been shared yet.'
    : 'No media has been shared yet.';

  const showReset = hasSearch || filter !== 'all' || scope !== 'all';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-12 text-center">
      <i
        className="fas fa-images text-4xl text-gray-300 dark:text-gray-600 mb-3 block"
        aria-hidden="true"
      />
      <p className="text-gray-500 dark:text-gray-400">{message}</p>
      <div className="mt-4 flex flex-col sm:flex-row gap-2 justify-center">
        {showReset && (
          <button
            type="button"
            onClick={onClear}
            className="inline-block bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-5 py-2 rounded-full text-sm font-medium transition"
          >
            <i className="fas fa-times mr-1" aria-hidden="true" />
            Clear filters
          </button>
        )}
        {scope !== 'mine' && !hasSearch && (
          <Link
            to="/"
            className="inline-block bg-orange hover:bg-orange-dark text-white px-5 py-2 rounded-full text-sm font-medium transition"
          >
            Go to feed to share
          </Link>
        )}
      </div>
    </div>
  );
};

/* ================================================================== */
/* Skeleton                                                            */
/* ================================================================== */

const MediaSkeleton = () => (
  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
    {Array.from({ length: 12 }).map((_, i) => (
      <div
        key={i}
        className="aspect-square rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse"
      />
    ))}
  </div>
);

export default Media;